import { mdiChevronRight } from "@mdi/js";
import { For, Match, Show, Switch } from "solid-js";
import { Buis } from "~/components/Buis";
import { Group } from "~/components/ui/group";
import { Icon } from "~/components/ui/icon";
import { Spacer } from "~/components/ui/spacer";
import { useDocumentContext } from "../../../providers/context";
import type { Article } from "../../../types";

function HeaderItem(props: TocItemProps) {
	const { setSelected, setSheetOpen } = useDocumentContext();

	const selectArticle = () => {
		if (!props.article.isViewable) return;
		setSelected(props.article);
		setSheetOpen(false);
	};

	return (
		<div
			class="bg-neutral-200 border-b p-4"
			data-id={props.article.id}
			onClick={selectArticle}
		>
			<Buis
				class="text-sm"
				value={props.article.tocSubtitle}
			/>
			<Buis value={props.article.tocTitle} />
			<Show when={props.article.isViewable}>
				<Icon
					class="min-w-6 fill-gray-400"
					size="sm"
					path={mdiChevronRight}
				/>
			</Show>
		</div>
	);
}

function SubheaderItem(props: TocItemProps) {
	const { setSelected, setSheetOpen } = useDocumentContext();

	const onClick = () => {
		if (!props.article.isViewable) return;
		setSelected(props.article);
		setSheetOpen(false);
	};

	return (
		<div
			class="bg-neutral-200/50 border-b p-4"
			data-id={props.article.id}
			onClick={onClick}
		>
			<Buis
				class="text-sm"
				value={props.article.tocSubtitle}
			/>
			<Buis value={props.article.tocTitle} />
			<Show when={props.article.isViewable}>
				<Icon
					class="min-w-6 fill-gray-400"
					size="sm"
					path={mdiChevronRight}
				/>
			</Show>
		</div>
	);
}

function ArticleItem(props: TocItemProps) {
	const { selected, setSelected, setSheetOpen } = useDocumentContext();

	const selectArticle = () => {
		setSelected(props.article);
		setSheetOpen(false);
	};

	const isSelected = props.article.id === selected()?.id;

	return (
		<Group
			class={"border-b py-4 px-4 transition-all"}
			data-id={props.article.id}
			aria-selected={selected()?.id === props.article.id}
			tabIndex={0}
			role="button"
			onClick={selectArticle}
			style={
				isSelected
					? {
							"border-left-color": props.article.headingColor || "hsl(var(--accent))",
							"border-left-width": "4px",
						}
					: undefined
			}
		>
			<div>
				<Buis
					class="mb-1.5 empty:mb-0 text-sm"
					value={props.article.tocSubtitle}
				/>
				<Buis value={props.article.tocTitle} />
			</div>
			<Spacer />
			<Icon
				class="min-w-6 fill-gray-400"
				size="sm"
				path={mdiChevronRight}
			/>
		</Group>
	);
}

export function DocumentTocMobile() {
	const { articles } = useDocumentContext();

	return (
		<For each={articles()}>
			{(article) => (
				<Switch fallback={<ArticleItem article={article} />}>
					<Match when={article.isHeading}>
						<HeaderItem article={article} />
					</Match>
					<Match when={article.isSubHeading}>
						<SubheaderItem article={article} />
					</Match>
				</Switch>
			)}
		</For>
	);
}

interface TocItemProps {
	article: Article;
}
